import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User, Lock, Shield, Bell, Save, Loader2, Eye, EyeOff, Camera, Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { AdminLayout } from "../../components/layout/AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const LearnerSettingsPage = () => {
  const { user: authUser } = useAuth();
  const variant = authUser?.role === "manager" ? "manager" : "learner";
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  // Profile form
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [bio, setBio] = useState("");

  // Password form
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);

  // Notification preferences
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [courseAssignNotifs, setCourseAssignNotifs] = useState(true);
  const [complianceNotifs, setComplianceNotifs] = useState(true);
  const [sessionNotifs, setSessionNotifs] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/user/profile");
        const data = res.data;
        setProfile(data);
        setFullName(data.full_name || "");
        setPhone(data.phone || "");
        setTitle(data.title || data.job_title || "");
        setBio(data.bio || "");
        setTwoFAEnabled(data.two_factor_enabled || false);
        const prefs = data.preferences || {};
        setEmailNotifs(prefs.email_notifications !== false);
        setCourseAssignNotifs(prefs.course_assignment_notifications !== false);
        setComplianceNotifs(prefs.compliance_notifications !== false);
        setSessionNotifs(prefs.session_notifications !== false);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchProfile();
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/user/profile", {
        full_name: fullName,
        phone,
        title,
        bio,
        preferences: {
          email_notifications: emailNotifs,
          course_assignment_notifications: courseAssignNotifs,
          compliance_notifications: complianceNotifs,
          session_notifications: sessionNotifs,
        },
      });
      toast.success("Profile updated successfully");
    } catch (e) {
      toast.error("Failed to update profile");
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await api.put("/user/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally { setSaving(false); }
  };

  const toggle2FA = async () => {
    const newState = !twoFAEnabled;
    setTwoFAEnabled(newState);
    try {
      await api.put("/user/profile", { two_factor_enabled: newState });
      toast.success(newState ? "2FA enabled" : "2FA disabled");
    } catch (e) {
      setTwoFAEnabled(!newState);
      toast.error("Failed to update 2FA setting");
    }
  };

  const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U";

  if (loading) {
    return (
      <AdminLayout title="Settings" variant={variant}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings" variant={variant}>
      <div className="space-y-6 max-w-3xl" data-testid="learner-settings-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Settings</h1>
          <p className="text-muted-foreground text-sm">Manage your profile, security, and preferences</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-1.5" /> Profile
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <Lock className="w-4 h-4 mr-1.5" /> Security
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-1.5" /> Notifications
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-4 space-y-4">
            {/* Avatar */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <Avatar className="w-20 h-20 border-2 border-primary/20">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold">
                        {getInitials(fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <button className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg" data-testid="change-avatar-btn">
                      <Camera className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{fullName}</h3>
                    <p className="text-sm text-muted-foreground">{profile?.email}</p>
                    <p className="text-xs text-muted-foreground capitalize mt-0.5">
                      {profile?.department || "No department"} · {profile?.role?.replace("_", " ") || "Learner"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profile Form */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input value={fullName} onChange={e => setFullName(e.target.value)} className="mt-1" data-testid="input-full-name" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1" placeholder="+1 (555) 000-0000" data-testid="input-phone" />
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1" placeholder="e.g. Operations Technician" data-testid="input-title" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={profile?.email || ""} disabled className="mt-1 bg-muted/50" />
                  </div>
                </div>
                <div>
                  <Label>Bio</Label>
                  <Textarea value={bio} onChange={e => setBio(e.target.value)} className="mt-1" placeholder="Tell us about yourself..." rows={3} data-testid="input-bio" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="mt-4 space-y-4">
            {/* Change Password */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Change Password
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Current Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showCurrent ? "text" : "password"}
                      value={currentPassword}
                      onChange={e => setCurrentPassword(e.target.value)}
                      data-testid="input-current-password"
                    />
                    <button onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      data-testid="input-new-password"
                    />
                    <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {newPassword && newPassword.length < 8 && (
                    <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
                  )}
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="mt-1"
                    data-testid="input-confirm-password"
                  />
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={changePassword}
                    disabled={saving || !currentPassword || !newPassword || newPassword !== confirmPassword}
                    data-testid="change-password-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                    Change Password
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 2FA */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Two-Factor Authentication
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Enable 2FA</p>
                    <p className="text-xs text-muted-foreground">Add an extra layer of security to your account</p>
                  </div>
                  <Switch checked={twoFAEnabled} onCheckedChange={toggle2FA} data-testid="toggle-2fa" />
                </div>
                {twoFAEnabled && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <p className="text-xs text-green-700">Two-factor authentication is enabled</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="w-4 h-4" /> Notification Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: "Email Notifications", desc: "Receive notifications via email", value: emailNotifs, set: setEmailNotifs, id: "email-notifs" },
                  { label: "Course Assignments", desc: "When you are assigned a new course", value: courseAssignNotifs, set: setCourseAssignNotifs, id: "course-notifs" },
                  { label: "Compliance Reminders", desc: "Reminders for upcoming compliance deadlines", value: complianceNotifs, set: setComplianceNotifs, id: "compliance-notifs" },
                  { label: "ILT Session Updates", desc: "Changes to your enrolled sessions", value: sessionNotifs, set: setSessionNotifs, id: "session-notifs" },
                ].map((pref) => (
                  <div key={pref.id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium">{pref.label}</p>
                      <p className="text-xs text-muted-foreground">{pref.desc}</p>
                    </div>
                    <Switch checked={pref.value} onCheckedChange={pref.set} data-testid={`toggle-${pref.id}`} />
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-notif-prefs-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default LearnerSettingsPage;
